import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {
    ChapterType,
    ICellChange,
    ICellChangeResponse,
    ICellOutput,
    IQuotationBlock,
    IQuotationCost,
    IQuotationRetail,
    IQuotationSection,
    IQuotationsQuotation,
    IQuotationTraveller,
    IQuotationTravellerPrice,
    IQuotationVatConfiguration,
    QuotationCellsActions
} from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import {RetailTableDataService} from "../services/retail-table-data/retail-table-data.service";
import {ITableHeaders, ITableRow, TableRowType} from "app_quotation/shared/services/table-data/ITable";
import {ConfigurationResourceService} from "libraries/service/resources/configuration/configurationResource.service";
import {CalculationMode, IWenji} from "libraries/interfaces/IWenji";
import {combineLatest, Observable, of, Subject} from "rxjs";
import {catchError, concatMap, map} from "rxjs/operators";
import {
    ICoordsTable,
    TableKeyboardNavigationService
} from 'libraries/service/table-keyboard-navigation/table-keyboard-navigation.service';
import {PermissionResourceService} from "libraries/service/resources/permission/permissionResource.service";
import {CachedQuotationService} from "app_quotation/shared/services/cached-quotation.service";
import {
    IMarginChangeEvent
} from "app_quotation/pages/retail/retail-grid/components/cell-total-margin/cell-total-margin.component";
import {ErrorTracingService} from 'libraries/service/error-tracing/error-tracing.service';
import {ActivatedRoute, ParamMap} from "@angular/router";
import {ERoute} from "app_quotation/shared/enums/ERoute";
import {EMoveMode} from "libraries/enums/e-move-mode";
import {VatService} from "app_quotation/shared/services/vat.service";
import {Yii} from "../../../../libraries/interfaces/Yii";
import {QuotationBlockCategoryCodeEnum} from "libraries/interfaces/quotation-block-category-code.enum";
import {CellActionUpdateService} from "./services/cell-action-update.service";
import {RefreshTableDataService} from "./services/refresh-table-data.service";
import {ICommissionCalculationConfig, IRetailCalculationConfig} from "./interfaces/ICalculationConfig";
import { EQuotationEntryPoints } from "libraries/enums/e-quotation-entry-points";

declare const wenji: IWenji;

const MIN_OPTION_COLSPAN: number = 2;

@Component({
    selector: 'retail-grid',
    templateUrl: './retail-grid.component.html',
    styleUrls: ["./retail-grid.component.scss", "../../../shared/styles/quotation-grid.scss"],
})
export class RetailGridComponent implements OnInit, AfterViewChecked, OnChanges {
    @Input()
    public quotation: IQuotationsQuotation;

    @Output()
    public readonly isXRateNoValid: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onError: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    public readonly quotationChange: EventEmitter<IQuotationsQuotation> = new EventEmitter<IQuotationsQuotation>();

    public tableData: ITableRow[];
    public tableHeaders: ITableHeaders;
    public displayedColumns: string[];
    public quotationCurrency: string;
    public tableRowType: typeof TableRowType = TableRowType;
    public focusCoords: ICoordsTable;
    public focusCoordsFromKeyboard: ICoordsTable;
    public rowIndexSubtract: number[];
    public colMax: number;
    public rowMax: number;
    public justCloseMarginTotal: boolean = false;
    public moveMode: EMoveMode = EMoveMode.grid;
    public isOptionPage: boolean = true;
    public isIncludedPage: boolean = true;

    public calculationMarginMarkupConfigDefault: IRetailCalculationConfig = {
        margin: 0.2,
        category: [
            { code: QuotationBlockCategoryCodeEnum.transportation, margin: 0.18 },
            { code: QuotationBlockCategoryCodeEnum.accomodation, margin: 0.24 }
        ]
    };

    public calculationCommissionConfigDefault: ICommissionCalculationConfig = {
        commission: 0.2,
        category: [
            { code: QuotationBlockCategoryCodeEnum.transportation, commission: 0.18 },
            { code: QuotationBlockCategoryCodeEnum.accomodation, commission: 0.24 }
        ]
    };

    public areVisibleBlocks: { [key: string]: boolean } = {
        [ChapterType.trip]: true,
        [ChapterType.fees]: true,
        [ChapterType.services]: true,
    };

    public calculationMode: CalculationMode;
    public commissionCalculationMode: CalculationMode = CalculationMode.commission;

    public profitLeftPosition: number;
    public vatLeftPosition: number;
    public totalRetailLeftPosition: number;
    public commissionTotalLeftPosition: number;

    /** Colspan for non-block-titles : section edging column + date column + block title column */
    public firstColspan: number = 3;
    public adjustmentColspan: number;
    public optionColspan: number = MIN_OPTION_COLSPAN;
    public chapterColspan: number;

    public quotationVatConfiguration: IQuotationVatConfiguration;

    public retailCalculationConfig: IRetailCalculationConfig;
    public commissionCalculationConfig: ICommissionCalculationConfig;
    public usedMarginMarkupCategories: Map<QuotationBlockCategoryCodeEnum, number>;
    public usedCommissionCategories: Map<QuotationBlockCategoryCodeEnum, number>;

    public isEditable: boolean;
    public commissionIsViewable: boolean;
    public commissionIsEditable: boolean;

    public subPage: EQuotationEntryPoints;
    public quotationEntryPoints: typeof EQuotationEntryPoints = EQuotationEntryPoints;

    private readonly cellChanges: Subject<ICellChange> = new Subject<ICellChange>();

    private usedCommission: number;
    private usedMargin: number;
    private usedMarkup: number;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly activatedRoute: ActivatedRoute,
        private readonly cachedQuotationService: CachedQuotationService,
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly permissionResourceService: PermissionResourceService,
        private readonly retailTableDataService: RetailTableDataService,
        private readonly tableKeyboardNavigationService: TableKeyboardNavigationService,
        private readonly vatService: VatService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly cellActionUpdateService: CellActionUpdateService,
        private readonly refreshTableDataService: RefreshTableDataService
    ) { }

    private static hasSomeCommissionValues(quotation: IQuotationsQuotation): boolean{
        return quotation.retail?.commission !== null
            && quotation.retail?.commission?.used !== null
            && !!quotation.sections?.filter(
                (section: IQuotationSection) : IQuotationBlock[] =>
                    section.blocks?.filter((block: IQuotationBlock) : boolean =>
                        block.retail?.commission !== null && block.retail?.commission?.used !== null)
            ).length;
    }

    public ngOnInit(): void {
        combineLatest(
            this.activatedRoute.paramMap,
            this.configurationResourceService.get(
                ['wui', 'shared_quotation', 'config', 'retail', 'retail.json'],
                this.calculationMarginMarkupConfigDefault),
            this.configurationResourceService.get(
                ['wui', 'shared_quotation', 'config', 'commission', 'commission.json'],
                this.calculationCommissionConfigDefault)
        )
            .subscribe(([parameters, retailConfig, commissionConfig]: [ParamMap, IRetailCalculationConfig, ICommissionCalculationConfig]) : void => {
                this.retailCalculationConfig = retailConfig;
                this.commissionCalculationConfig = commissionConfig;
                this.subPage = parameters.get(ERoute.subPage) as EQuotationEntryPoints;
                this.isOptionPage = this.subPage === this.quotationEntryPoints.option;
                this.isIncludedPage = this.subPage === this.quotationEntryPoints.included;
                this.calculationMode = wenji.quotation.retailCalculationMode;

                this.permissionResourceService
                    .hasPermission('quotation.retail.edit')
                    .subscribe((isEditable: boolean): boolean => this.isEditable = isEditable);

                this.permissionResourceService.getPermissionsStartingWith(['quotation', 'commission'])
                    .subscribe((commissionPermissions: Set<string>): void => {
                        this.commissionIsViewable = commissionPermissions.has('quotation.commission.view') && RetailGridComponent.hasSomeCommissionValues(this.quotation);
                        this.commissionIsEditable = commissionPermissions.has('quotation.commission.use');
                        // initializing grid after retrieving commission permission has those permissions have an impact on grid display
                        this.initGrid(this.quotation);
                });
            });

        this.cellChanges
            .pipe(
                // it's important to use concatMap here to avoid parallel execution of requests
                concatMap((cellChange: ICellChange): Observable<ICellChangeResponse> => {
                    return this.handleCellChange(cellChange)
                        .pipe(
                            //#32285 - setting again margin and commission global when user goes from "Included" to "Option", if not, value is lost
                            // Errors are handled at this point to avoid blocking the whole
                            // UI if an error occurs somewhere (for instance, in the API)
                            catchError((error: Error): Observable<ICellChangeResponse> => {
                                this.errorTracingService.captureException(error);
                                return of(undefined);
                            })
                        );
                })
            )
            .subscribe(
                {
                    next: (cellChangeResponse: ICellChangeResponse | undefined): void => {
                        if(cellChangeResponse){
                            this.updateAfterCellChange(cellChangeResponse);
                            if (cellChangeResponse.quotation) {
                                this.quotationChange.emit(cellChangeResponse.quotation);
                            }
                            if (cellChangeResponse.quotation && !cellChangeResponse.quotation.isXRateValid) {
                                this.isXRateNoValid.emit();
                            }
                        } else {
                            this.isEditable = false;
                            this.onError.emit(Yii.t('wui-shared-js', 'Problème réseau rencontré. Veuillez rafraîchir la page.'));
                        }
                    },
                    error: (error: Error): void => {
                        this.errorTracingService.captureException(error);
                    }
                });
    }

    public ngAfterViewChecked(): void {
        this.initGridStyles();
    }

    public ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges.quotation &&
            simpleChanges.quotation.currentValue &&
            !simpleChanges.quotation.isFirstChange()) {
            this.initGrid(simpleChanges.quotation.currentValue);
            this.initGridStyles();
        }
    }

    public initGridStyles(): void {
        if (this.calculationMode) {
            const offsetHeightTableHead: number = this.document.getElementById('table-head').offsetHeight;
            const offsetHeightRow0: number = this.document.getElementById('row0').offsetHeight;
            this.document.getElementById('row0').style.top = `${offsetHeightTableHead}px`;
            this.document.getElementById('row1').style.top = `${offsetHeightTableHead + offsetHeightRow0}px`;
        }
    }

    public initGrid(quotation: IQuotationsQuotation): void {
        this.quotationVatConfiguration = this.vatService.getVatConfiguration(this.quotation);
        // #32285 - recording "included" rate values when we go to option page to reset them when we return to "included" page
        if(this.isOptionPage && this.tableData !== undefined && this.tableData[0].totalRetail){
            if(this.tableData[0].totalRetail.commission?.used){
                this.usedCommission = this.tableData[0].totalRetail.commission.used;
            }
            if(this.tableData[0].totalRetail.margin?.used){
                this.usedMargin = this.tableData[0].totalRetail.margin.used;
            }
            if(this.tableData[0].totalRetail.markup?.used){
                this.usedMarkup = this.tableData[0].totalRetail.markup.used;
            }
        }

        this.tableData = this.retailTableDataService.computeTableData(quotation, this.subPage);

        if(this.isIncludedPage){
            this.setLostRates();
        }

        this.usedMarginMarkupCategories = this.retailTableDataService.extractCategoryMarginMarkupCommission(
            this.tableData, this.retailCalculationConfig, this.calculationMode);
        if(this.commissionIsViewable) {
            this.usedCommissionCategories = this.retailTableDataService.extractCategoryMarginMarkupCommission(
                this.tableData, this.commissionCalculationConfig, this.commissionCalculationMode);
        }
        this.tableHeaders = this.retailTableDataService.computeTableHeaders(
            quotation, this.calculationMode, this.subPage, this.quotationVatConfiguration, this.commissionIsViewable);
        this.displayedColumns = computeDisplayedColumns(this.tableHeaders);
        this.createRowIndex(this.tableData);

        this.quotationCurrency = quotation.cost.currency;

        this.initColspan(quotation, this.quotationVatConfiguration);
    }

    public setTotalRetailLeftPosition(leftPosition: number, origin?: string): void {
        if (!this.totalRetailLeftPosition || leftPosition !== this.totalRetailLeftPosition) {
            this.totalRetailLeftPosition = Math.ceil(leftPosition);
            this.changeDetectorRef.detectChanges();
        }
    }

    public setPositionsFromGain(gainRightPosition: number): void {
        if(this.commissionIsViewable){
            if(this.quotationVatConfiguration?.vatRate){
                this.vatLeftPosition = gainRightPosition;
            }else{
                this.commissionTotalLeftPosition = gainRightPosition;
            }
        }else{
            this.setTotalRetailLeftPosition(gainRightPosition);
        }
    }
    public setPositionsFromCommissionTotal(cellRightPosition: number): void {

        if(this.quotationVatConfiguration?.vatRate){
            this.vatLeftPosition = cellRightPosition;
        }else{
            this.setTotalRetailLeftPosition(cellRightPosition);
        }
    }

    public collapse(chapterType: ChapterType, isVisible: boolean): void {
        this.areVisibleBlocks[chapterType] = isVisible;
    }

    // TODO+LA : create single function (multiple : one block, one quotation...) and pass parameter (from string enum) to call common services functions
    public changeQuotationProfit(price: IQuotationCost): void {
        this.emitCellChange({
            price,
            itemType: QuotationCellsActions.updateQuotationProfit,
        });
    }

    public changeQuotationMargin(event: IMarginChangeEvent): void {
        this.emitCellChange({
            itemType: QuotationCellsActions.updateQuotationMargin,
            calculationMode: event.calculationMode,
            marginRequest: event.marginRequest
        });
    }

    public changeQuotationCommission(event: IMarginChangeEvent): void {
        this.emitCellChange({
            itemType: QuotationCellsActions.updateQuotationCommission,
            calculationMode: event.calculationMode,
            commissionRequest: event.marginRequest
        });
    }

    public changeBlockMargin(event: { retail: IQuotationRetail, calculationMode: CalculationMode }, totalPriceId: number, blockId: number): void {
        const output: ICellOutput = {
            retail: event.retail,
            itemType: event.calculationMode === CalculationMode.margin ?
                QuotationCellsActions.updateBlockMargin :
                QuotationCellsActions.updateBlockMarkup,
            id: totalPriceId,
            blockId,
        };
        this.emitCellChange(output);
    }

    public changeBlockCommission(event: { retail: IQuotationRetail, calculationMode: CalculationMode }, totalPriceId: number, blockId: number): void {
        const output: ICellOutput = {
            retail: event.retail,
            itemType: QuotationCellsActions.updateQuotationCommission,
            id: totalPriceId,
            blockId,
        };
        this.emitCellChange(output);
    }

    public changeBlockProfit(profit: IQuotationCost, totalPriceId: number, blockId: number): void {
        const output: ICellOutput = {
            price: profit,
            itemType: QuotationCellsActions.updateBlockProfit,
            id: totalPriceId,
            blockId,
        };
        this.emitCellChange(output);
    }

    public changeBlockRetail(retail: IQuotationRetail, totalPriceId: number, blockId: number): void {
        const output: ICellOutput = {
            retail,
            itemType: QuotationCellsActions.updateBlockRetail,
            id: totalPriceId,
            blockId,
        };
        this.emitCellChange(output);
    }

    public changeQuotationTotalRetail(price: IQuotationCost): void {
        this.emitCellChange({
            price,
            itemType: QuotationCellsActions.updateQuotationTotalRetail,
        });
    }

    public changeQuotationAdjustment(adjustment: IQuotationCost): void {
        this.emitCellChange({
            price: adjustment,
            itemType: QuotationCellsActions.updateQuotationAdjustment,
        });
    }

    public changeQuotationVatConfiguration(vatConfiguration: IQuotationVatConfiguration): void {
        this.emitCellChange({
            vatConfiguration,
            itemType: QuotationCellsActions.updateQuotationVatConfiguration,
        });
    }

    public changeTravellerTotalRetail(retail: IQuotationRetail, travellerId: number): void {
        this.emitCellChange({
            retail,
            travellerId,
            itemType: QuotationCellsActions.updateTravellerTotalRetail,
        });
    }

    public changeTravellerAdjustment(adjustment: IQuotationRetail, travellerId: number): void {
        this.emitCellChange({
            price: adjustment,
            travellerId,
            itemType: QuotationCellsActions.updateTravellerAdjustment,
        });
    }

    public emitCellChange(output: ICellOutput): void {
        this.cellChanges.next({ output });
    }
    // TODO+LA : stop

    public isHeader(rowType: TableRowType): boolean {
        return Boolean(rowType === this.tableRowType.chapterHeader);
    }

    public createRowIndex(tableData: ITableRow[]): void {
        let subtract: number = 0;

        this.rowIndexSubtract = tableData.map((data: ITableRow, index: number): number => {
            if (this.isHeader(data.type)) {
                subtract++;
            }

            return index - subtract;
        });

        this.setTableBoundaries();
    }

    public onMoveModeChange(moveMode: EMoveMode): void {
        this.moveMode = moveMode;
    }

    public emitFocusChange(coords: ICoordsTable): void {
        this.focusCoords = coords;
    }

    public onKeyup(keyEvent: KeyboardEvent): void {
        let colNumberCell: number = this.colMax;
        if (this.focusCoords.rowIndex > 1 && this.isIncludedPage) {
            colNumberCell = 2;
        }

        if ((this.moveMode === EMoveMode.grid || this.moveMode === EMoveMode.cell) && this.focusCoords) {
            switch (keyEvent.key) {
                case ("ArrowLeft"):
                case ("Left"):
                    if (this.focusCoords.rowIndex === 1 && this.focusCoords.colIndex === 2 && this.isIncludedPage) {
                        this.focusCoordsFromKeyboard = { rowIndex: 0, colIndex: this.colMax };
                        this.focusCoords = this.focusCoordsFromKeyboard;
                    } else if (this.focusCoords.rowIndex === 2 && this.focusCoords.colIndex === 0 && this.isIncludedPage) {
                        this.focusCoordsFromKeyboard = { rowIndex: 1, colIndex: this.colMax };
                        this.focusCoords = this.focusCoordsFromKeyboard;
                    } else {
                        this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveLeft(this.focusCoords, colNumberCell);
                        this.focusCoords = this.focusCoordsFromKeyboard;
                        break;
                    }
                    break;
                case ("ArrowRight"):
                case ("Right"):
                    this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveRight(this.focusCoords, this.rowMax, colNumberCell);
                    this.focusCoords = this.focusCoordsFromKeyboard;
                    break;
                case ("ArrowUp"):
                case ("Up"):
                    this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveUp(this.focusCoords);
                    this.focusCoords = this.focusCoordsFromKeyboard;
                    break;
                case ("ArrowDown"):
                case ("Down"):
                    this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveDown(this.focusCoords, this.rowMax);
                    this.focusCoords = this.focusCoordsFromKeyboard;
                    break;
                default:
                    break;
            }
        }

        this.enterNavigation(keyEvent);
        this.focusCellEmpty();
    }

    /// ngFor trackers
    public travellerPriceTrackBy(index: number, item: IQuotationTravellerPrice): number {
        return item.traveller; // or item.id
    }

    public travellerTrackBy(index: number, item: IQuotationTraveller): number {
        return item.id; // or item.id
    }

    // Cell change handlers
    private handleCellChange(cellChange: ICellChange): Observable<ICellChangeResponse> {
        this.cellActionUpdateService.setInitValues(this.quotation, this.quotationVatConfiguration, this.subPage);
        // tslint:disable-next-line:switch-default
        switch (cellChange.output.itemType) {
            case QuotationCellsActions.updateQuotationProfit:
                return this.cellActionUpdateService.updateQuotationProfit(cellChange);
            case QuotationCellsActions.updateQuotationTotalRetail:
                return this.cellActionUpdateService.updateQuotationTotalRetail(cellChange);
            case QuotationCellsActions.updateQuotationMargin:
                return this.cellActionUpdateService.updateQuotationMargin(cellChange);
            case QuotationCellsActions.updateQuotationCommission:
                return this.cellActionUpdateService.updateQuotationCommission(cellChange);
            case QuotationCellsActions.updateQuotationAdjustment:
                return this.cellActionUpdateService.updateQuotationAdjustment(cellChange);
            case QuotationCellsActions.updateQuotationVatConfiguration:
                return this.cellActionUpdateService.updateQuotationVatConfiguration(cellChange);
            case QuotationCellsActions.updateTravellerTotalRetail:
                return this.cellActionUpdateService.updateTravellerTotalRetail(cellChange);
            case QuotationCellsActions.updateTravellerAdjustment:
                return this.cellActionUpdateService.updateTravellerAdjustment(cellChange);
            case QuotationCellsActions.updateBlockMargin:
                return this.cellActionUpdateService.updateBlockMargin(cellChange);
            case QuotationCellsActions.updateBlockMarkup:
                return this.cellActionUpdateService.updateBlockMarkup(cellChange);
            case QuotationCellsActions.updateBlockProfit:
                return this.cellActionUpdateService.updateBlockProfit(cellChange);
            case QuotationCellsActions.updateBlockRetail:
                return this.cellActionUpdateService.updateBlockRetail(cellChange);
        }
    }

    private updateAfterCellChange(cellChangeResponse: ICellChangeResponse | undefined): void {
        switch (cellChangeResponse && cellChangeResponse.itemType) {
            case QuotationCellsActions.updateBlockMarkup:
            case QuotationCellsActions.updateBlockMargin:
            case QuotationCellsActions.updateBlockProfit:
            case QuotationCellsActions.updateBlockRetail:
                if (this.subPage === EQuotationEntryPoints.included) {
                    this.refreshTableDataService.refreshTravellersOnTotalRow(cellChangeResponse, this.tableData, this.isIncludedPage);
                    this.refreshTableDataService.refreshQuotationRows(cellChangeResponse, this.tableData);
                }
                this.refreshTableDataService.refreshBlockRow(cellChangeResponse, this.tableData);
                break;
            case QuotationCellsActions.updateTravellerTotalRetail:
            case QuotationCellsActions.updateTravellerAdjustment:
                this.refreshTableDataService.refreshQuotationRows(cellChangeResponse, this.tableData);
                this.refreshTableDataService.refreshTravellerTotalRowsById(cellChangeResponse, this.tableData);
                break;
            case QuotationCellsActions.updateQuotationCommission:
                if (this.subPage === EQuotationEntryPoints.included) {
                    this.refreshTableDataService.refreshTravellersOnTotalRow(cellChangeResponse, this.tableData, this.isIncludedPage);
                    this.refreshTableDataService.refreshQuotationRows(cellChangeResponse, this.tableData);
                }
                this.refreshTableDataService.refreshAllBlockRows(cellChangeResponse, this.tableData);
                break;
            case QuotationCellsActions.updateQuotationMargin:
                if (this.subPage === EQuotationEntryPoints.included) {
                    this.refreshTableDataService.refreshTravellersOnTotalRow(cellChangeResponse, this.tableData, this.isIncludedPage);
                    this.refreshTableDataService.refreshQuotationRows(cellChangeResponse, this.tableData);
                }
                this.refreshTableDataService.refreshAllBlockRows(cellChangeResponse, this.tableData);
                break;
            case QuotationCellsActions.updateQuotationVatConfiguration:
                this.refreshTableDataService.refreshTravellersOnTotalRow(cellChangeResponse, this.tableData, this.isIncludedPage);
                this.refreshTableDataService.refreshQuotationRows(cellChangeResponse, this.tableData);
                this.refreshTableDataService.refreshAllBlockRows(cellChangeResponse, this.tableData);
                this.refreshVatConfiguration(cellChangeResponse);
                break;
            case QuotationCellsActions.updateQuotationProfit:
            case QuotationCellsActions.updateQuotationTotalRetail:
            case QuotationCellsActions.updateQuotationAdjustment:
            default:
                this.refreshTableDataService.refreshTravellersOnTotalRow(cellChangeResponse, this.tableData, this.isIncludedPage);
                this.refreshTableDataService.refreshQuotationRows(cellChangeResponse, this.tableData);
                break;
        }
        if(this.isIncludedPage){
            this.usedCommission = cellChangeResponse.quotation.retail.commission?.used ?? undefined;
            this.usedMargin = cellChangeResponse.quotation.retail.margin?.used ?? undefined;
            this.usedMarkup = cellChangeResponse.quotation.retail.markup?.used ?? undefined;
        }
    }

    private setTableBoundaries(): void {
        this.colMax = this.isIncludedPage ? (this.quotation.travellers || []).length + 2 : 2;
        this.rowMax = this.rowIndexSubtract[this.rowIndexSubtract.length - 1];
    }

    // TODO+LA : extract to common service with cost-grid
    // Navigation
    private enterNavigation(keyEvent: KeyboardEvent): void {
        if ((this.moveMode === EMoveMode.cell || this.moveMode === EMoveMode.insideCell) && keyEvent.key === "Enter") {
            if (this.justCloseMarginTotal) {
                this.justCloseMarginTotal = false;
            }
            if (this.moveMode === EMoveMode.cell) {
                this.moveMode = EMoveMode.insideCell;
            } else {
                this.focusCoordsFromKeyboard = this.tableKeyboardNavigationService.moveNextLine(this.focusCoords, this.rowMax, this.colMax);
                this.focusCoords = this.focusCoordsFromKeyboard;
                this.moveMode = EMoveMode.grid;
            }
        }

    }

    private focusCellEmpty(): void {
        if (this.focusCoords.rowIndex === 1 && (this.focusCoords.colIndex < 2) && this.isIncludedPage) {
            this.focusCoords = { rowIndex: 1, colIndex: 2 };
        } else if (this.focusCoords.rowIndex === 2 && this.focusCoords.colIndex > 2 && this.isIncludedPage) {
            this.focusCoords = { rowIndex: 2, colIndex: 2 };
        }
    }

    private refreshVatConfiguration(cellChangeResponse: ICellChangeResponse): void {
        this.quotationVatConfiguration = this.vatService.getVatConfiguration(cellChangeResponse.quotation);
        this.tableHeaders = this.retailTableDataService.computeTableHeaders(
            cellChangeResponse.quotation, this.calculationMode, this.subPage, this.quotationVatConfiguration, this.commissionIsViewable);
        this.displayedColumns = computeDisplayedColumns(this.tableHeaders);
        this.initColspan(cellChangeResponse.quotation, this.quotationVatConfiguration);
    }

    private initColspan(quotation: IQuotationsQuotation, vatConfiguration: IQuotationVatConfiguration): void {
        // Adjusting colspan thanks to commission visibility
        const adjustmentColspan: number = this.commissionIsViewable ? 5 : 3;
        const chapterColspan: number = this.commissionIsViewable ? 6 : 4;
        this.adjustmentColspan = this.vatService.getValueAccordingToVat(vatConfiguration, adjustmentColspan, adjustmentColspan + 1);

        const minChapterColspan: number = this.vatService.getValueAccordingToVat(vatConfiguration, chapterColspan, chapterColspan + 1);
        this.chapterColspan = quotation.travellers ? (minChapterColspan + quotation.travellers.length) : minChapterColspan;

        if(quotation.travellers){
            this.optionColspan = MIN_OPTION_COLSPAN + quotation.travellers.length;
        } else {
            this.optionColspan = MIN_OPTION_COLSPAN;
        }
    }

    private setLostRates(): void {
        if(this.usedCommission && this.tableData[0].totalRetail.commission?.used){
            this.tableData[0].totalRetail.commission.used = this.usedCommission;
        }
        if(this.usedMargin && this.tableData[0].totalRetail.margin?.used){
            this.tableData[0].totalRetail.margin.used = this.usedMargin;
        }
        if(this.usedMarkup && this.tableData[0].totalRetail.markup?.used){
            this.tableData[0].totalRetail.markup.used = this.usedMarkup;
        }
    }
}

function computeDisplayedColumns(headers: ITableHeaders): string[] {
    return [...Object.keys(headers), 'end'];
}
